/**
 * Coverage schemas — insurance during intake.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type {
  CoverageRelationship as FhirCoverageRelationship,
  CoverageStatus as FhirCoverageStatus,
} from '@unify/fhir-model/resources/coverage';

/** FHIR Coverage.status values — synced with fhir-model. */
export const coverageStatusValues = [
  'active',
  'cancelled',
  'entered-in-error',
] as const satisfies readonly FhirCoverageStatus[];

/** Subscriber-to-beneficiary relationship values — synced with fhir-model. */
export const subscriberRelationshipValues = [
  'self',
  'spouse',
  'child',
  'other',
] as const satisfies readonly FhirCoverageRelationship[];

/**
 * Intake insurance coverage creation.
 */
export const intakeCoverageSchema = z.object({
  patientId: z.string().min(1, 'Patient is required'),
  /** Selected payer Organization id (Coverage.payor reference). */
  payerOrganizationId: z.string().min(1, 'Insurance payer is required'),
  /** Payer display name (Coverage.payor display). */
  payorName: z.string().min(1, 'Insurance payer is required'),
  memberId: z.string().min(1, 'Member ID is required'),
  groupNumber: z.string().optional(),
  subscriberRelationship: z.enum(subscriberRelationshipValues),
  subscriberFirstName: z.string().optional(),
  subscriberLastName: z.string().optional(),
  subscriberDateOfBirth: z.string().optional(),
  /** Coverage effective dates (YYYY-MM-DD). */
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
});

export type IntakeCoverageInput = z.infer<typeof intakeCoverageSchema>;
